import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { Collapse } from 'bootstrap';
import styles from './Accordion.module.css';

export enum AccordionVariants {
    variant1 = 'accordion-variant-1',
    variant2 = 'accordion-variant-2',
}

export interface AccordionProps {
    id?: string;
    classes?: string;
    borders?: boolean;
    buttonClasses?: string;
    bodyClasses?: string;
    variant?: AccordionVariants;
    /* for accordions that open from down to top */
    arrowOnClosed?: 'up' | 'down';
    items: {
        id: string;
        buttonContent: string | JSX.Element;
        buttonOptions?: JSX.Element;
        bodyContent: string | JSX.Element | JSX.Element[];
    }[];
}

export interface AccordionRef {
    open: (id: string) => void;
    close: (id: string) => void;
}

export const Accordion = forwardRef<AccordionRef, AccordionProps>(
    ({ id, classes, borders, buttonClasses, bodyClasses, variant, arrowOnClosed, items }, ref) => {
        const [activeItems, setActiveItems] = useState<string[]>([]);

        const openItem = (id: string) => {
            const elem = document.getElementById(id);
            if (elem) {
                const collapse = Collapse.getOrCreateInstance(elem);
                collapse.show();
            }
        };

        const closeItem = (id: string) => {
            const elem = document.getElementById(id);
            if (elem) {
                const collapse = Collapse.getOrCreateInstance(elem);
                collapse.hide();
            }
        };

        // Expose these functions to parent components via ref
        useImperativeHandle(ref, () => ({
            open: (id: string) => {
                // if id is already active, do nothing
                if (activeItems.includes(id)) {
                    return;
                }
                setActiveItems((prevActiveItems) => {
                    openItem(id);
                    return [...prevActiveItems, id];
                });
            },
            close: (id: string) => {
                setActiveItems((prevActiveItems) => {
                    if (prevActiveItems.includes(id)) {
                        closeItem(id);
                        return prevActiveItems.filter((itemId) => itemId !== id);
                    }
                    return prevActiveItems;
                });
            },
        }));

        const handleItemClick = (id: string) => {
            setActiveItems((prevActiveItems) => {
                if (prevActiveItems.includes(id)) {
                    closeItem(id);
                    return prevActiveItems.filter((itemId) => itemId !== id);
                } else {
                    openItem(id);
                    return [...prevActiveItems, id];
                }
            });
        };

        return (
            <div
                id={id || undefined}
                className={`accordion accordion-flush ${styles[variant || AccordionVariants.variant1]} ${classes || ''}`}
                data-testid="accordion">
                {items.map((item, index) => (
                    <div
                        key={`accordion-item-${index}`}
                        className={`accordion-item ${styles['accordion-item']} ${borders ? 'border' : ''}`}>
                        <button
                            type="button"
                            className={`accordion-button ${styles['accordion-button']} ${arrowOnClosed === 'up' ? styles['inverted-arrow'] : ''} ${!activeItems.includes(item.id) ? `collapsed ${styles['collapsed']}` : ''} ${buttonClasses || ''}`}
                            onClick={() => handleItemClick(item.id)}>
                            {item.buttonContent}
                        </button>
                        {item.buttonOptions && <div className={styles['options-container']}>{item.buttonOptions}</div>}
                        <div id={item.id} className="accordion-collapse collapse">
                            <div className={`accordion-body ${styles['accordion-body']} ${bodyClasses}`}>{item.bodyContent}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }
);
